/**
 * kzk-components.js — Web Components shared by more than one kzk-* section.
 *
 * - KzkMoney: Shopify money formatting (used by the vehicle finder and the cost calculator)
 * - <kzk-pill-group>: exclusive aria-pressed toggle buttons (vehicle finder, date presets, FAQ filter)
 * - <kzk-country-check>: Green Card destination check (sections 5 and 22)
 *
 * Loaded as a module so repeated <script type="module"> tags execute it once.
 */

/**
 * @typedef {Object} KzkCountry
 * @property {string} key
 * @property {string} code
 * @property {string} name
 * @property {'ok'|'star'|'excluded'} status
 */

const KzkMoney = {
  /**
   * Formats cents with a Shopify money format string.
   * @param {number} cents
   * @param {string} [format]
   * @returns {string}
   */
  format(cents, format = '{{amount}} €') {
    const value = Number(cents) / 100;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount';
    /** @type {string} */
    let formatted;
    const group = (whole, sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = group(String(Math.round(value)), ',');
        break;
      case 'amount_with_comma_separator': {
        const [whole, dec] = value.toFixed(2).split('.');
        formatted = `${group(whole, '.')},${dec}`;
        break;
      }
      case 'amount_no_decimals_with_comma_separator':
        formatted = group(String(Math.round(value)), '.');
        break;
      case 'amount_with_space_separator': {
        const [whole, dec] = value.toFixed(2).split('.');
        formatted = `${group(whole, ' ')},${dec}`;
        break;
      }
      case 'amount_no_decimals_with_space_separator':
        formatted = group(String(Math.round(value)), ' ');
        break;
      case 'amount_with_apostrophe_separator': {
        const [whole, dec] = value.toFixed(2).split('.');
        formatted = `${group(whole, "'")}.${dec}`;
        break;
      }
      default: {
        const [whole, dec] = value.toFixed(2).split('.');
        formatted = `${group(whole, ',')}.${dec}`;
      }
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted).replace(/<[^>]+>/g, '');
  },

  /**
   * Formats a signed difference ("+ 2,00 €").
   * @param {number} cents
   * @param {string} [format]
   * @returns {string}
   */
  delta(cents, format) {
    const sign = cents < 0 ? '− ' : '+ ';
    return `${sign}${KzkMoney.format(Math.abs(cents), format)}`;
  },
};

/**
 * Normalises a string for loose matching: lower case, no accents, ß → ss.
 * @param {string} value
 * @returns {string}
 */
function kzkNormalize(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Reads a JSON script child.
 * @param {Element} root
 * @param {string} selector
 * @returns {any}
 */
function kzkReadJson(root, selector) {
  const script = root.querySelector(selector);
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || 'null');
  } catch {
    return null;
  }
}

class KzkPillGroup extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLButtonElement[]} */
  get buttons() {
    return Array.from(this.querySelectorAll('button[aria-pressed]'));
  }

  /** @returns {string|null} */
  get value() {
    const pressed = this.buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
    return pressed ? pressed.dataset.value || null : null;
  }

  /**
   * Selects a value programmatically (or clears with null).
   * @param {string|null} value
   * @param {boolean} [silent]
   */
  select(value, silent = false) {
    const match = value === null ? null : this.buttons.find((button) => button.dataset.value === value) || null;
    this.selectButton(match, silent);
  }

  /**
   * Presses exactly one pill (or none). Several pills may share a value, so the pill itself decides.
   * @param {HTMLButtonElement|null} target
   * @param {boolean} [silent]
   */
  selectButton(target, silent = false) {
    for (const button of this.buttons) {
      button.setAttribute('aria-pressed', String(button === target));
    }
    const value = target ? target.dataset.value ?? '' : null;
    if (!silent) {
      this.dispatchEvent(new CustomEvent('kzk:change', { detail: { value, button: target }, bubbles: true }));
    }
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[aria-pressed]') : null;
    if (!button || !this.contains(button)) return;
    const toggle = this.hasAttribute('data-toggle');
    if (toggle && button.getAttribute('aria-pressed') === 'true') {
      this.selectButton(null);
      return;
    }
    this.selectButton(button);
  };
}

class KzkCountryCheck extends HTMLElement {
  #controller = new AbortController();
  /** @type {KzkCountry[]} */
  #countries = [];
  /** @type {Record<string, string>} */
  #strings = {};
  #timer = 0;
  /** Index of the highlighted option while the list is open */
  #active = -1;

  connectedCallback() {
    this.#countries = kzkReadJson(this, '[data-kzk-countries]') || [];
    this.#strings = kzkReadJson(this, '[data-kzk-strings]') || {};
    const { signal } = this.#controller;
    const input = this.input;
    const button = this.querySelector('[data-kzk-check]');
    if (!input) return;
    this.#setupList(signal);

    if (button) {
      button.addEventListener('click', () => this.check(), { signal });
      input.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.check();
          }
        },
        { signal }
      );
      input.addEventListener('input', () => this.#markFilled(), { signal });
    } else {
      input.addEventListener(
        'input',
        () => {
          this.#markFilled();
          window.clearTimeout(this.#timer);
          this.#timer = window.setTimeout(() => this.check(), 220);
        },
        { signal }
      );
    }
    if (input.value) this.check();
  }

  disconnectedCallback() {
    this.#controller.abort();
    window.clearTimeout(this.#timer);
  }

  /** @returns {HTMLInputElement|null} */
  get input() {
    const input = this.querySelector('input[data-kzk-country-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #markFilled() {
    const input = this.input;
    if (!input) return;
    if (input.value.trim()) input.setAttribute('data-filled', '');
    else input.removeAttribute('data-filled');
  }

  /** @returns {HTMLElement|null} */
  get list() {
    const list = this.querySelector('[data-kzk-country-list]');
    return list instanceof HTMLElement ? list : null;
  }

  /**
   * Dropdown with the countries: shown while the field has focus (all countries, filtered
   * while typing), navigated with the arrow keys, closed with Escape, on choosing or on
   * leaving the component.
   * @param {AbortSignal} signal
   */
  #setupList(signal) {
    const input = this.input;
    const list = this.list;
    if (!input || !list) return;

    input.addEventListener('focus', () => this.openList(input.value), { signal });
    input.addEventListener(
      'click',
      () => {
        if (list.hidden) this.openList(input.value);
      },
      { signal }
    );
    input.addEventListener('input', () => this.openList(input.value), { signal });
    input.addEventListener(
      'keydown',
      (event) => {
        const open = !list.hidden;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (!open) this.openList(input.value);
          this.#move(event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Escape' && open) {
          event.preventDefault();
          this.closeList();
        } else if (event.key === 'Enter' && open && this.#active >= 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#choose(this.#active);
        }
      },
      { signal }
    );
    list.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
    list.addEventListener(
      'click',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[data-index]') : null;
        if (option instanceof HTMLElement) this.#choose(Number(option.dataset.index));
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !this.contains(next)) this.closeList();
      },
      { signal }
    );
  }

  /** @type {KzkCountry[]} countries currently listed */
  #listed = [];

  /**
   * Opens the list, filtered by the query (empty = all countries).
   * @param {string} query
   */
  openList(query) {
    const input = this.input;
    const list = this.list;
    if (!input || !list) return;
    const q = kzkNormalize(query);
    this.#listed = q
      ? this.#countries.filter((c) => kzkNormalize(c.name).includes(q) || kzkNormalize(c.code).startsWith(q))
      : this.#countries.slice();
    const id = list.id || 'kzk-country-list';
    list.replaceChildren();
    if (this.#listed.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'kzk-combobox__empty';
      empty.textContent = list.dataset.empty || '';
      list.append(empty);
    }
    this.#listed.forEach((country, index) => {
      const item = document.createElement('li');
      item.className = 'kzk-combobox__option' + (country.status === 'excluded' ? ' kzk-combobox__option--excluded' : '');
      item.id = `${id}-${index}`;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.dataset.index = String(index);
      const code = document.createElement('span');
      code.className = 'kzk-combobox__code';
      code.textContent = country.code;
      const name = document.createElement('span');
      name.textContent = country.name;
      item.append(code, name);
      list.append(item);
    });
    list.hidden = false;
    this.#active = -1;
    input.setAttribute('aria-expanded', 'true');
  }

  closeList() {
    const input = this.input;
    const list = this.list;
    if (!input || !list || list.hidden) return;
    list.hidden = true;
    this.#active = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  /** @param {number} step */
  #move(step) {
    const list = this.list;
    const input = this.input;
    if (!list || !input || this.#listed.length === 0) return;
    const count = this.#listed.length;
    this.#active = (this.#active + step + count) % count;
    const options = list.querySelectorAll('[data-index]');
    options.forEach((option, index) => {
      const active = index === this.#active;
      option.toggleAttribute('data-active', active);
      option.setAttribute('aria-selected', String(active));
      if (active) {
        input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /** @param {number} index */
  #choose(index) {
    const country = this.#listed[index];
    const input = this.input;
    if (!country || !input) return;
    input.value = country.name;
    this.#markFilled();
    this.closeList();
    window.clearTimeout(this.#timer);
    this.check();
  }

  /**
   * Finds a country by code or (partial) name.
   * @param {string} query
   * @returns {KzkCountry|null}
   */
  find(query) {
    const q = kzkNormalize(query);
    if (!q) return null;
    const byCode = this.#countries.find((country) => kzkNormalize(country.code) === q);
    if (byCode) return byCode;
    const exact = this.#countries.find((country) => kzkNormalize(country.name) === q);
    if (exact) return exact;
    const starts = this.#countries.filter((country) => kzkNormalize(country.name).startsWith(q));
    if (starts.length === 1) return starts[0];
    const contains = this.#countries.filter((country) => kzkNormalize(country.name).includes(q));
    if (contains.length === 1) return contains[0];
    return starts[0] || null;
  }

  check() {
    const input = this.input;
    if (!input) return;
    const query = input.value.trim();
    const result = this.querySelector('[data-kzk-result]');
    if (!result) return;
    const title = result.querySelector('[data-kzk-result-title]');
    const text = result.querySelector('[data-kzk-result-text]');
    const icons = result.querySelectorAll('[data-kzk-result-icon]');
    const chips = this.querySelectorAll('[data-kzk-chip]');
    const ctas = this.querySelectorAll('[data-kzk-cta]');
    const s = this.#strings;

    for (const chip of chips) chip.removeAttribute('data-active');

    /** @type {'empty'|'ok'|'excluded'|'unknown'} */
    let state = 'empty';
    let country = null;
    if (query) {
      country = this.find(query);
      if (!country) state = 'unknown';
      else state = country.status === 'excluded' ? 'excluded' : 'ok';
    }

    result.dataset.state = state;
    for (const icon of icons) {
      icon.hidden = icon.getAttribute('data-kzk-result-icon') !== state;
    }
    for (const cta of ctas) {
      const wanted = (cta.getAttribute('data-kzk-cta') || '').split(' ');
      cta.hidden = !wanted.includes(state);
    }

    if (state === 'empty') {
      if (title) title.textContent = '';
      if (text) text.textContent = s.empty || '';
      return;
    }

    const fill = (template, value) => String(template || '[land]').split('[land]').join(value);
    if (state === 'unknown') {
      if (title) title.textContent = fill(s.unknownTitle, query);
      if (text) text.textContent = fill(s.unknownText, query);
      return;
    }

    const label = `${country.code} · ${country.name}`;
    if (state === 'excluded') {
      if (title) title.textContent = fill(s.excludedTitle, label);
      if (text) text.textContent = s.excludedText || '';
      for (const chip of chips) {
        if (chip.getAttribute('data-kzk-chip') === country.key) chip.setAttribute('data-active', '');
      }
      return;
    }

    if (title) title.textContent = fill(s.okTitle, label);
    if (text) {
      text.textContent = country.status === 'star' && s.starText ? `${s.okText || ''} ${s.starText}`.trim() : s.okText || '';
    }
    this.dispatchEvent(new CustomEvent('kzk:country', { detail: { country, state }, bubbles: true }));
  }
}

if (!customElements.get('kzk-pill-group')) customElements.define('kzk-pill-group', KzkPillGroup);
if (!customElements.get('kzk-country-check')) customElements.define('kzk-country-check', KzkCountryCheck);

window.KzkMoney = KzkMoney;
window.kzkNormalize = kzkNormalize;
window.kzkReadJson = kzkReadJson;

export { KzkMoney, KzkPillGroup, KzkCountryCheck, kzkNormalize, kzkReadJson };
